import { Megaphone } from 'lucide-react';
import { Link, Navigate, Outlet, ScrollRestoration, useLocation } from 'react-router-dom';

import { BootGate } from '../../app/boot';
import { useSession } from '../../app/session';
import { useSite } from '../../app/site';
import { cn } from '../../lib/cn';
import { useLocale } from '../../lib/useLocale';
import { GameTicketSync } from '../../features/auth/GameTicketSync';
import { IncompleteProfileScreen } from '../../features/auth/IncompleteProfileScreen';
import { isIntroPending } from '../../features/auth/registerWizard';
import { BanNotice, MaintenanceScreen } from '../common/States';
import { Button } from '../ui/Button';
import { Footer } from './Footer';
import { Header } from './Header';

function AnnouncementBar() {
  const { t, text } = useLocale();
  const { settings } = useSite();
  const announcement = settings?.announcement;
  const copy =
    text({ zh: announcement?.textZh ?? '', en: announcement?.textEn ?? '' }) || announcement?.text || '';
  if (!announcement?.enabled || !copy) return null;

  return (
    <div
      className={cn(
        'border-b px-4 py-2.5 text-center text-xs sm:px-6',
        'border-primary/20 bg-primary-soft text-primary',
      )}
    >
      <span className="mx-auto flex w-full max-w-4xl items-center justify-center gap-x-2 gap-y-1">
        <Megaphone className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="eyebrow shrink-0 text-current opacity-70">{t('home.announcementLabel')}</span>
        <span className="min-w-0 text-left">{copy}</span>
      </span>
    </div>
  );
}

export function AppShell() {
  const { t, text } = useLocale();
  const { settings, isMaintenance, isLoading: siteLoading } = useSite();
  const { viewer, isAuthenticated, isRestoring: sessionRestoring } = useSession();
  const location = useLocation();
  const boot = <BootGate siteLoading={siteLoading} sessionRestoring={sessionRestoring} />;
  const maintenanceMessage =
    text({ zh: settings?.maintenance.messageZh ?? '', en: settings?.maintenance.messageEn ?? '' }) ||
    settings?.maintenance.message ||
    '';

  // Staff can still sign in during maintenance; everyone else sees the notice.
  if (isMaintenance && !isAuthenticated) {
    return (
      <>
        <GameTicketSync />
        {boot}
        <div className="relative">
          <MaintenanceScreen
            message={maintenanceMessage}
            siteName={settings?.siteName ?? t('common.appName')}
          />
          <div className="absolute inset-x-0 bottom-10 flex justify-center">
            <Button asChild variant="outline" size="sm">
              <Link to="/login">{t('common.signIn')}</Link>
            </Button>
          </div>
        </div>
      </>
    );
  }

  if (viewer?.profileIncomplete) {
    return (
      <>
        <GameTicketSync />
        {boot}
        <IncompleteProfileScreen />
        <ScrollRestoration />
      </>
    );
  }

  // New-signup page 2: the account is already complete, so the incomplete-profile
  // lock will not hold them. Keep them on /register until they save or 跳过.
  if (isIntroPending() && location.pathname !== '/register') {
    return (
      <>
        <GameTicketSync />
        {boot}
        <Navigate to="/register" replace />
      </>
    );
  }

  return (
    <>
      <GameTicketSync />
      {boot}
      <div className="flex min-h-screen min-w-0 flex-col">
      <a
        href="#main"
        className="sr-only-focusable bg-primary text-primary-contrast fixed top-3 left-3 z-50 rounded-sm px-3 py-2 text-sm font-medium"
      >
        {t('nav.skipToContent')}
      </a>
      <AnnouncementBar />
      <Header />
      {viewer?.ban ? <BanNotice ban={viewer.ban} /> : null}
      <main id="main" className="min-w-0 flex-1">
        <Outlet />
      </main>
      <Footer />
      <ScrollRestoration />
    </div>
    </>
  );
}

/** Standard page frame: consistent gutters, max width and vertical rhythm. */
export function PageContainer({
  children,
  className,
  width = 'default',
}: {
  children: React.ReactNode;
  className?: string;
  width?: 'default' | 'narrow' | 'wide';
}) {
  return (
    <div
      className={cn(
        'mx-auto w-full px-4 py-10 sm:px-6 lg:px-8',
        width === 'narrow' && 'max-w-3xl',
        width === 'default' && 'max-w-7xl',
        width === 'wide' && 'max-w-8xl',
        className,
      )}
    >
      {children}
    </div>
  );
}
